import { useEffect, useState } from 'react';
import type { TafProfileInfo } from '../../../../types/network';
import type { SnapshotMeterValue } from '../../../../store/historicalPlaybackStore';
import { getSmgwHistory } from '../../../../services/maasService';
import { parseUtcTimestamp } from '../../../../utils/timeUtils';
import { OBIS_EXPORT, OBIS_IMPORT, formatPower, formatTime, powerColor } from '../../../../utils/meterFormat';
import { fmt } from '../../../../utils/numberFormat';
import type { MeterDirection } from '../../../../utils/smgwUtils';
import { PhaseRotationBadge } from '../smgw/PhaseRotationBadge';

/**
 * Tooltip of the "geschätzt" badge by the measurement pool's source tag: which simulation basis
 * produced the value (load profile per 15-min slot vs. flat annual average vs. PV neighbour scaling).
 */
const estimateTooltip = (source: string | undefined): string => {
  if (source === 'pv_neighbor_estimate') {
    return 'Geschätzter Wert: aus den gemessenen PV-Nachbaranlagen der Zone skaliert und gegen die Trafo-Messung plausibilisiert — kein Messwert dieses Zählers.';
  }
  if (source?.startsWith('load_profile_simulation')) {
    return 'Simulierter Wert aus dem Lastprofil des Anschlusses (Standardlastprofil zur Uhrzeit und zum Wochentag, skaliert mit dem Jahresverbrauch) — keine Messung dieses Zählers.';
  }
  if (source?.startsWith('annual_consumption') || source?.startsWith('fallback_simulation')) {
    return 'Simulierter Wert: Jahresmittel aus dem Jahresverbrauch, ohne Tagesgang — keine Messung dieses Zählers.';
  }
  return 'Geschätzter Wert — keine Messung dieses Zählers.';
};

/** Exact snapshot meter input of one Messpunkt (15-min value at the rewound instant). */
export const ExactMeterValue = ({ value, effectiveTs }: { value: SnapshotMeterValue; effectiveTs: string }) => {
  const kw = value.powerW / 1000;
  const d = parseUtcTimestamp(effectiveTs);
  const isEstimate = value.simulated === true || value.source != null;
  return (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className={`text-base font-bold font-mono whitespace-nowrap ${powerColor(value.powerW)}`}>{fmt(kw)} kW</span>
      <span
        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isEstimate
          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
          : 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'}`}
        title={isEstimate ? estimateTooltip(value.source) : 'Gemessener Zählerwert, mit dem der archivierte Lastfluss gerechnet wurde.'}
      >
        {isEstimate ? 'geschätzt' : 'gemessen'}
      </span>
      <span
        className="ml-auto text-xs text-gray-400 dark:text-primary-400 whitespace-nowrap"
        title="Archivierter Zähler-Wert zum Berechnungszeitpunkt (15-Minuten-Wert) — Eingangsgröße des gespeicherten Lastflusses."
      >
        {d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} {d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
      </span>
    </div>
  );
};

/**
 * Meter value at the selected HISTORICAL instant (map time-rewind): the last
 * archived hourly value of this Messpunkt in the 24 h up to the anchor.
 * Fallback for snapshots without exact meterValues.
 */
export const HistoricalMeterValue = ({ serialNumber, tafLogicalName, anchorIso, direction }: {
  serialNumber: string;
  tafLogicalName?: string;
  anchorIso: string;
  direction?: MeterDirection;
}) => {
  const [state, setState] = useState<{ powerW: number; obis: string; ts: string; minW: number; maxW: number } | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    getSmgwHistory(serialNumber, '24h', tafLogicalName, anchorIso)
      .then(data => {
        if (cancelled) return;
        let best: { powerW: number; obis: string; ts: string; minW: number; maxW: number } | null = null;
        for (const series of data.series ?? []) {
          // Only active-power series — voltages, currents etc. are no meter value
          if (series.obisCode !== OBIS_IMPORT && series.obisCode !== OBIS_EXPORT && series.obisCode !== '1-0:16.7.0') continue;
          const last = series.dataPoints[series.dataPoints.length - 1];
          if (!last) continue;
          if (!best || parseUtcTimestamp(last.timestamp) > parseUtcTimestamp(best.ts)) {
            const sign = series.obisCode === OBIS_EXPORT ? -1 : 1;
            best = {
              powerW: sign * last.avgValue,
              obis: series.obisCode,
              ts: last.timestamp,
              minW: Math.min(sign * last.minValue, sign * last.maxValue),
              maxW: Math.max(sign * last.minValue, sign * last.maxValue),
            };
          }
        }
        setState(best);
      })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [serialNumber, tafLogicalName, anchorIso]);

  if (state === 'loading') {
    return <div className="mt-1 text-xs text-gray-400 dark:text-primary-400">Lade historischen Wert …</div>;
  }
  if (state == null) {
    return (
      <div className="mt-1 text-xs text-gray-400 dark:text-primary-400">
        {direction === 'einspeisung'
          ? 'Keine Erzeugungs-Messreihe im Archiv — Einspeisung fehlt in der Berechnung.'
          : 'Keine Archiv-Messreihe für diesen Messpunkt.'}
      </div>
    );
  }
  const kw = state.powerW / 1000;
  const d = parseUtcTimestamp(state.ts);
  // The archive is HOURLY — label the value as the hour average so nobody
  // expects it to equal the 15-min loadflow value shown above.
  const hour = d.getHours();
  const rangeW = state.maxW - state.minW;
  const showRange = rangeW > Math.max(200, Math.abs(state.powerW) * 0.1);
  return (
    <div className="mt-1">
      <div className="flex items-center justify-between">
        <div className={`text-base font-bold font-mono ${powerColor(state.powerW)}`}>
          {fmt(kw)} kW
          {state.obis === OBIS_EXPORT && <span className="ml-1 text-xs font-normal text-success-600 dark:text-success-400">Einspeisung</span>}
        </div>
        <span
          className="text-xs text-orange-600 dark:text-orange-300"
          title="Stundenmittel aus dem Zähler-Archiv. Die Leistung oben zeigt den 15-Minuten-Lastflusswert — beide dürfen voneinander abweichen."
        >
          {d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} Ø {String(hour).padStart(2, '0')}–{String((hour + 1) % 24).padStart(2, '0')} Uhr
        </span>
      </div>
      {showRange && (
        <div className="text-xs text-gray-400 dark:text-primary-400" title="Minimum und Maximum der Werte innerhalb dieser Stunde.">
          Spanne der Stunde: {fmt(state.minW / 1000)} bis {fmt(state.maxW / 1000)} kW
        </div>
      )}
    </div>
  );
};

/** Live TAF-10 values of one meter: power, Bezug/Einspeisung, per-phase table, frequency, cos φ. */
export const TafLiveDetails = ({ live, fresh }: { live: TafProfileInfo; fresh: boolean }) => (
  <div className="space-y-2">
    <div className="flex items-baseline justify-between">
      <div className={`text-base font-bold font-mono ${powerColor(live.powerW)}`}>{formatPower(live.powerW)}</div>
      <span
        className={`text-xs ${fresh ? 'text-gray-400 dark:text-primary-400' : 'text-orange-600 dark:text-orange-300'}`}
        title={fresh ? 'Zeitpunkt des letzten TAF-10-Werts' : 'Der letzte TAF-10-Wert ist älter als das Live-Fenster'}
      >
        {fresh ? '' : 'letzte Messung '}{formatTime(live.timestamp)}
      </span>
    </div>
    <div className="grid grid-cols-2 gap-2 text-xs">
      <div className="bg-gray-50 dark:bg-primary-700 rounded px-2 py-1.5">
        <div className="text-gray-500 dark:text-primary-400">Bezug (+P)</div>
        <div className={`font-mono font-medium text-sm ${live.activePowerImportW != null ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`}>
          {live.activePowerImportW != null ? formatPower(live.activePowerImportW) : '-'}
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-primary-700 rounded px-2 py-1.5">
        <div className="text-gray-500 dark:text-primary-400">Einspeisung (−P)</div>
        <div className={`font-mono font-medium text-sm ${live.activePowerExportW != null ? 'text-success-600 dark:text-success-400' : 'text-gray-400'}`}>
          {live.activePowerExportW != null ? formatPower(live.activePowerExportW) : '-'}
        </div>
      </div>
    </div>
    <div>
      {live.phaseRotation ? (
        <div className="mb-1.5"><PhaseRotationBadge rotation={live.phaseRotation} size="pill" /></div>
      ) : null}
      <div className="grid grid-cols-5 gap-1 text-xs text-gray-500 dark:text-primary-400 mb-1 px-1">
        <div>Phase</div>
        <div className="text-right">U (V)</div>
        <div className="text-right">I (A)</div>
        <div className="text-right">P (kW)</div>
        <div className="text-right">cos φ</div>
      </div>
      <div className="space-y-1">
        {([
          { phase: 'L1', voltage: live.voltageL1V, current: live.currentL1A, power: live.activePowerL1W, pf: live.powerFactorL1, color: 'text-red-600 dark:text-red-400' },
          { phase: 'L2', voltage: live.voltageL2V, current: live.currentL2A, power: live.activePowerL2W, pf: live.powerFactorL2, color: 'text-yellow-600 dark:text-yellow-400' },
          { phase: 'L3', voltage: live.voltageL3V, current: live.currentL3A, power: live.activePowerL3W, pf: live.powerFactorL3, color: 'text-blue-600 dark:text-blue-400' },
        ]).map(({ phase, voltage, current, power, pf, color }) => (
          <div key={phase} className="grid grid-cols-5 gap-1 text-xs bg-gray-50 dark:bg-primary-700 px-1.5 py-1 rounded">
            <div className={`font-medium ${color}`}>{phase}</div>
            <div className="text-right text-gray-700 dark:text-gray-300 font-mono">{voltage != null ? fmt(voltage, 1) : '-'}</div>
            <div className="text-right text-gray-700 dark:text-gray-300 font-mono">{current != null ? fmt(current, 1) : '-'}</div>
            <div className={`text-right font-mono ${power != null ? powerColor(power) : 'text-gray-400'}`}>{power != null ? fmt(power / 1000) : '-'}</div>
            <div className="text-right text-gray-500 dark:text-gray-400 font-mono">{pf != null ? fmt(pf) : '-'}</div>
          </div>
        ))}
      </div>
    </div>
    <div className="pt-1.5 border-t border-gray-100 dark:border-primary-500 flex justify-between text-xs text-gray-500 dark:text-primary-400">
      <div>Frequenz: <span className="font-mono text-gray-700 dark:text-gray-300">{live.frequencyHz != null ? `${fmt(live.frequencyHz)} Hz` : '-'}</span></div>
      <div>cos φ (ges): <span className="font-mono text-gray-700 dark:text-gray-300">{live.powerFactorTotal != null ? fmt(live.powerFactorTotal) : '-'}</span></div>
    </div>
  </div>
);
